import { ScopeType } from "sockapi/scope-types";
import { ScopeIdentifierType, Scope } from "sockapi/scopes";
import {
  SockAPIScopeType,
  SockAPIScopeIdentifierType,
  SockAPIScopeState,
} from "sockapi/scope-state";
import {
  SockAPIUpdatableScopeType,
  SockAPIScopeUpdatePayload,
} from "sockapi/scope-updates";

import {
  DummyScopeSpec,
  dummyScopeHooks,
} from "sockapi/scope-specs/dummy-spec";
import {
  HuntAdminScopeSpec,
  huntAdminScopeHooks,
} from "sockapi/scope-specs/hunt-admin-spec";
import {
  ServerHealthScopeSpec,
  serverHealthScopeHooks,
} from "sockapi/scope-specs/server-health-spec";
import {
  BigBoardScopeSpec,
  bigBoardScopeHooks,
} from "sockapi/scope-specs/big-board-spec";
import {
  TeamListScopeSpec,
  teamListScopeHooks,
} from "sockapi/scope-specs/team-list-spec";
import {
  TeamMembersScopeSpec,
  teamMembersScopeHooks,
} from "sockapi/scope-specs/team-members-spec";
import { TeamScopeSpec, teamScopeHooks } from "sockapi/scope-specs/team-spec";
import {
  TeamAnswerableScopeSpec,
  teamAnswerableScopeHooks,
} from "sockapi/scope-specs/team-answerable-spec";
import {
  PuzzleContentScopeSpec,
  puzzleContentScopeHooks,
} from "sockapi/scope-specs/puzzle-content-spec";
import {
  ErratumContentScopeSpec,
  erratumContentScopeHooks,
} from "sockapi/scope-specs/erratum-content-spec";
import {
  TeamErrataScopeSpec,
  teamErrataScopeHooks,
} from "sockapi/scope-specs/team-errata-spec";
import {
  SolutionContentScopeSpec,
  solutionContentScopeHooks,
} from "sockapi/scope-specs/solution-content-spec";
import {
  CannedHintScopeSpec,
  cannedHintScopeHooks,
} from "sockapi/scope-specs/canned-hint-spec";
import {
  TeamCannedHintsScopeSpec,
  teamCannedHintsScopeHooks,
} from "sockapi/scope-specs/team-canned-hints-spec";
import {
  TeamHintsScopeSpec,
  teamHintsScopeHooks,
} from "sockapi/scope-specs/team-hints-spec";
import {
  KeyframeScopeSpec,
  keyframeScopeHooks,
} from "sockapi/scope-specs/keyframe-spec";
import {
  TeamCutscenesScopeSpec,
  teamCutscenesScopeHooks,
} from "sockapi/scope-specs/team-cutscenes-spec";
import { HintScopeSpec, hintScopeHooks } from "sockapi/scope-specs/hint-spec";
import {
  MockDirectBackendScopeSpec,
  mockDirectBackendScopeHooks,
} from "sockapi/scope-specs/mock-direct-backend-spec";
import {
  AdminQueueScopeSpec,
  adminQueueScopeHooks,
} from "sockapi/scope-specs/admin-queue-spec";
import {
  AdminQueueTaskScopeSpec,
  adminQueueTaskScopeHooks,
} from "sockapi/scope-specs/admin-queue-task-spec";
import {
  TeamCollectiblesScopeSpec,
  teamCollectiblesScopeHooks,
} from "sockapi/scope-specs/team-collectibles-spec";
import {
  TeamTitlesScopeSpec,
  teamTitlesScopeHooks,
} from "sockapi/scope-specs/team-titles-spec";
import {
  StateDownloadScopeSpec,
  stateDownloadScopeHooks,
} from "sockapi/scope-specs/state-download-spec";
import {
  AdminStatsScopeSpec,
  adminStatsScopeHooks,
} from "sockapi/scope-specs/admin-stats-spec";
import {
  StatsScopeSpec,
  statsScopeHooks,
} from "sockapi/scope-specs/stats-spec";
import {
  HuntStatsScopeSpec,
  huntStatsScopeHooks,
} from "sockapi/scope-specs/hunt-stats-spec";
import {
  PublicSubmissionsScopeSpec,
  publicSubmissionsScopeHooks,
} from "sockapi/scope-specs/public-submissions-spec";
import {
  RoundScopeSpec,
  roundScopeHooks,
} from "sockapi/scope-specs/round-spec";
import {
  teamBlueTheatreScopeHooks,
  TeamBlueTheatreScopeSpec,
} from "sockapi/scope-specs/team-blue-theatre-spec";
import {
  TeamTimedUnlockScopeSpec,
  teamTimedUnlockScopeHooks,
} from "sockapi/scope-specs/team-timed-unlock-spec";

export type ScopeSpecs = {
  [ScopeType.DUMMY]: DummyScopeSpec;
  [ScopeType.HUNT_ADMIN]: HuntAdminScopeSpec;
  [ScopeType.SERVER_HEALTH]: ServerHealthScopeSpec;
  [ScopeType.BIG_BOARD]: BigBoardScopeSpec;
  [ScopeType.TEAM_LIST]: TeamListScopeSpec;
  [ScopeType.TEAM_MEMBERS]: TeamMembersScopeSpec;
  [ScopeType.TEAM]: TeamScopeSpec;
  [ScopeType.TEAM_ANSWERABLE]: TeamAnswerableScopeSpec;
  [ScopeType.PUZZLE_CONTENT]: PuzzleContentScopeSpec;
  [ScopeType.ERRATUM_CONTENT]: ErratumContentScopeSpec;
  [ScopeType.TEAM_ERRATA]: TeamErrataScopeSpec;
  [ScopeType.SOLUTION_CONTENT]: SolutionContentScopeSpec;
  [ScopeType.CANNED_HINT]: CannedHintScopeSpec;
  [ScopeType.HINT]: HintScopeSpec;
  [ScopeType.TEAM_CANNED_HINTS]: TeamCannedHintsScopeSpec;
  [ScopeType.TEAM_HINTS]: TeamHintsScopeSpec;
  [ScopeType.KEYFRAME]: KeyframeScopeSpec;
  [ScopeType.TEAM_CUTSCENES]: TeamCutscenesScopeSpec;
  [ScopeType.MOCK_DIRECT_BACKEND]: MockDirectBackendScopeSpec;
  [ScopeType.ADMIN_QUEUE]: AdminQueueScopeSpec;
  [ScopeType.ADMIN_QUEUE_TASK]: AdminQueueTaskScopeSpec;
  [ScopeType.TEAM_COLLECTIBLES]: TeamCollectiblesScopeSpec;
  [ScopeType.TEAM_TITLES]: TeamTitlesScopeSpec;
  [ScopeType.STATE_DOWNLOAD]: StateDownloadScopeSpec;
  [ScopeType.ADMIN_STATS]: AdminStatsScopeSpec;
  [ScopeType.STATS]: StatsScopeSpec;
  [ScopeType.HUNT_STATS]: HuntStatsScopeSpec;
  [ScopeType.PUBLIC_SUBMISSIONS]: PublicSubmissionsScopeSpec;
  [ScopeType.ROUND]: RoundScopeSpec;
  [ScopeType.TEAM_BLUE_THEATRE]: TeamBlueTheatreScopeSpec;
  [ScopeType.TEAM_TIMED_UNLOCK]: TeamTimedUnlockScopeSpec;
};

export type ScopeIdComponents = (string | number)[];

type ScopeHooksBase<TScopeType extends ScopeType> = {
  identityFunc: (scope: Scope & { type: TScopeType }) => Scope<TScopeType>;
};
type ScopeIdentifierHooks<TScopeType extends ScopeIdentifierType> = {
  getIdComponents: (scope: Scope<TScopeType>) => ScopeIdComponents;
};
type UpdatableScopeHooks<TScopeType extends SockAPIUpdatableScopeType> = {
  update: (
    scopeState: SockAPIScopeState<TScopeType>,
    payload: SockAPIScopeUpdatePayload<TScopeType>
  ) => void;
};

export type ScopeHooks<TScopeType extends ScopeType> =
  ScopeHooksBase<TScopeType> &
    (TScopeType extends ScopeIdentifierType
      ? ScopeIdentifierHooks<TScopeType>
      : unknown) &
    (TScopeType extends SockAPIUpdatableScopeType
      ? UpdatableScopeHooks<TScopeType>
      : unknown) & {
      disableClientCache?: true;
    };

export const scopeHooks: {
  [TScopeType in ScopeType]: ScopeHooks<TScopeType>;
} = {
  [ScopeType.DUMMY]: dummyScopeHooks,
  [ScopeType.HUNT_ADMIN]: huntAdminScopeHooks,
  [ScopeType.SERVER_HEALTH]: serverHealthScopeHooks,
  [ScopeType.BIG_BOARD]: bigBoardScopeHooks,
  [ScopeType.TEAM_LIST]: teamListScopeHooks,
  [ScopeType.TEAM_MEMBERS]: teamMembersScopeHooks,
  [ScopeType.TEAM]: teamScopeHooks,
  [ScopeType.TEAM_ANSWERABLE]: teamAnswerableScopeHooks,
  [ScopeType.PUZZLE_CONTENT]: puzzleContentScopeHooks,
  [ScopeType.ERRATUM_CONTENT]: erratumContentScopeHooks,
  [ScopeType.TEAM_ERRATA]: teamErrataScopeHooks,
  [ScopeType.SOLUTION_CONTENT]: solutionContentScopeHooks,
  [ScopeType.CANNED_HINT]: cannedHintScopeHooks,
  [ScopeType.HINT]: hintScopeHooks,
  [ScopeType.TEAM_CANNED_HINTS]: teamCannedHintsScopeHooks,
  [ScopeType.TEAM_HINTS]: teamHintsScopeHooks,
  [ScopeType.KEYFRAME]: keyframeScopeHooks,
  [ScopeType.TEAM_CUTSCENES]: teamCutscenesScopeHooks,
  [ScopeType.MOCK_DIRECT_BACKEND]: mockDirectBackendScopeHooks,
  [ScopeType.ADMIN_QUEUE]: adminQueueScopeHooks,
  [ScopeType.ADMIN_QUEUE_TASK]: adminQueueTaskScopeHooks,
  [ScopeType.TEAM_COLLECTIBLES]: teamCollectiblesScopeHooks,
  [ScopeType.TEAM_TITLES]: teamTitlesScopeHooks,
  [ScopeType.STATE_DOWNLOAD]: stateDownloadScopeHooks,
  [ScopeType.ADMIN_STATS]: adminStatsScopeHooks,
  [ScopeType.STATS]: statsScopeHooks,
  [ScopeType.HUNT_STATS]: huntStatsScopeHooks,
  [ScopeType.PUBLIC_SUBMISSIONS]: publicSubmissionsScopeHooks,
  [ScopeType.ROUND]: roundScopeHooks,
  [ScopeType.TEAM_BLUE_THEATRE]: teamBlueTheatreScopeHooks,
  [ScopeType.TEAM_TIMED_UNLOCK]: teamTimedUnlockScopeHooks,
};

// Type specializations to convince typescript that the respective
// hooks satisfy their specific type requirements.
// Note that we cannot rely on the key sets of these dictionaries
// for type checking since they are typed more narrowly than the
// key set of scopeHooks.
export const updatableScopeHooks: {
  [TScopeType in SockAPIUpdatableScopeType]: UpdatableScopeHooks<TScopeType>;
} = scopeHooks;
export const globalScopeHooks: {
  [TScopeType in Exclude<SockAPIScopeType, SockAPIScopeIdentifierType>]: {
    identityFunc: (scope: { type: TScopeType }) => Scope<TScopeType>;
  };
} = scopeHooks;
